// The agent's permission modes, as pure functions.
//
// Three modes, cycled by the user with Shift+Tab in the chat composer:
//
//   auto     the agent acts unattended — the behaviour that shipped, and what
//            every project that predates this module reads as.
//   manual   every mutating call is offered to the user first. Reads are never
//            gated, so inspecting state stays free.
//   plan     the agent gets read-only tools plus `set_plan`. It works out what
//            it would do, declares it, and waits for approval.
//
// WHY THIS IS ITS OWN MODULE. `ChatPanel` cannot be rendered under jest (it pulls
// the SSE client, the whole page's dependency graph and ReactFlow), so anything
// living inside it is untestable — the same reason `chatRounds.js` and
// `chatMessages.js` were extracted. What is in here is exactly the part that must
// not be wrong: which calls get gated.
//
// CHANGE ONE, CHANGE BOTH with `product_backend/app/services/agent_modes.py`.
// The cycle ORDER is part of the contract, not a detail: the chip tells the user
// what Shift+Tab will select next, so a different order on the two sides would
// show them one mode and enforce another. Pinned by `chatModes.test.js` and
// `tests/test_agent_run_modes.py`.

export const MODE_AUTO = 'auto';
export const MODE_MANUAL = 'manual';
export const MODE_PLAN = 'plan';

// Auto first because it is the default and where most sessions stay; plan last so
// the cycle reads as "loosen, tighten, stop and think", and one more press returns
// to normal working.
export const AGENT_MODES = [MODE_AUTO, MODE_MANUAL, MODE_PLAN];

export const DEFAULT_AGENT_MODE = MODE_AUTO;

// What the selector shows. `hint` is the one-line explanation of what the mode
// actually does, since "Manual" alone does not say what is manual — it is the
// trigger's title attribute and the menu row's second line.
//
// LABEL AND COLOUR ONLY — no icon. There used to be a `▶`/`✋`/`📋` per mode, and
// they were dropped rather than replaced with SVGs: emoji are drawn by the
// platform's colour font, so CSS `color` cannot restyle them (the trap
// `components/icons.js` documents), which means the glyph fought the very per-mode
// colour that distinguishes these three. Three short words need no glyph.
export const MODE_META = {
  [MODE_AUTO]: {
    label: 'Auto',
    hint: 'The assistant works on its own and does not ask before acting.',
  },
  [MODE_MANUAL]: {
    label: 'Manual',
    hint: 'You approve each change before it happens. Reading is not gated.',
  },
  [MODE_PLAN]: {
    label: 'Plan',
    hint: 'The assistant proposes a plan and changes nothing until you approve it.',
  },
};

// Tool-effect vocabulary, mirroring `chat.py`'s `TOOL_EFFECTS` values. The effect
// of each call arrives ON the `tool_call` SSE event, so there is deliberately NO
// table of 125 tool names here — see `effectOf`.
export const EFFECT_READ = 'read';
export const EFFECT_PROTOCOL = 'protocol';
export const EFFECT_WRITE = 'write';
export const EFFECT_LAUNCH = 'launch';

/**
 * Coerce a stored or server-supplied mode to a known one.
 *
 * Anything unrecognised — including `undefined` from a project row written before
 * the field existed — reads as `auto`. Deliberately NOT coerced to a restrictive
 * mode: a project silently switching itself into manual would look like the agent
 * had stopped working, and the user never asked for it.
 */
export function normalizeMode(mode) {
  return AGENT_MODES.includes(mode) ? mode : DEFAULT_AGENT_MODE;
}

/** The next mode in the Shift+Tab cycle. */
export function nextMode(mode) {
  const i = AGENT_MODES.indexOf(normalizeMode(mode));
  return AGENT_MODES[(i + 1) % AGENT_MODES.length];
}

/**
 * What approving a plan returns the session to.
 *
 * Approving a plan LEAVES plan mode — that is what starts the build — so something
 * has to decide which mode it lands in. `previousMode` is whatever the user was in
 * before they switched to plan, remembered by the caller (plan mode itself carries
 * no memory), so a user who was in MANUAL before planning keeps their approvals
 * instead of being silently dropped into unattended `auto`.
 *
 * `plan` in means there is nothing to return to — a first-time plan, or a project
 * that has only ever been in plan mode — and that resolves to `auto`.
 *
 * Pure, and here rather than in a component, because BOTH the editor's `ChatPanel`
 * band and the dashboard's plan panel approve plans now. Two copies of this rule
 * would diverge in exactly one direction: the copy that forgets `manual` hands a
 * user who asked to approve every change an agent that no longer asks.
 */
export function planExitMode(previousMode) {
  const previous = normalizeMode(previousMode);
  return previous === MODE_PLAN ? MODE_AUTO : previous;
}

export function modeLabel(mode) {
  return MODE_META[normalizeMode(mode)].label;
}

export function modeMeta(mode) {
  return MODE_META[normalizeMode(mode)];
}

/**
 * What a tool call does, from the SSE event that announced it.
 *
 * The classification lives on the BACKEND (`chat.py`'s `TOOL_EFFECTS`, asserted
 * complete at import) and rides on the `tool_call` event, so this file needs no
 * mirror of 125 tool names — the repo already carries three such hand-kept mirrors
 * and this would have been the largest by far.
 *
 * A MISSING `effect` defaults to `write`, which is the fail-safe direction: an
 * older backend that does not send the field makes the client ask too OFTEN, never
 * act unasked.
 */
export function effectOf(toolCall) {
  const effect = toolCall?.effect;
  return effect === EFFECT_READ || effect === EFFECT_PROTOCOL || effect === EFFECT_LAUNCH
    ? effect
    : EFFECT_WRITE;
}

/**
 * The gate: may this call run, or must the user decide first?
 *
 * Returns `'allow'`, `'ask'` or `'refuse'`. The three verdicts mirror
 * `agent_runner/approval.py`, and the distinction between the last two matters:
 * a REFUSE keeps the turn going (the model can adapt within its round budget),
 * while an ASK stops it until a person answers.
 *
 * `approvedTools` is the "don't ask again for this tool" set — the coarse grant,
 * held for the session, which is what keeps a twelve-tile pipeline build from
 * being forty clicks.
 */
export function decideToolGate({ effect, mode, toolName, approvedTools }) {
  // Never gated, in any mode:
  //  * PROTOCOL — the agent's own control channel. `complete_tile` is how the
  //    orchestration loop advances and `request_user_input` is how a subagent
  //    reaches the user, so gating either DEADLOCKS the turn rather than
  //    restraining it. `set_plan` is the tool plan mode exists to collect.
  //  * READ — this is what keeps manual mode usable. A BC subagent has to call
  //    `list_faces` before it can propose anything, and asking about that would
  //    put a dozen clicks in front of the first decision that matters.
  if (effect === EFFECT_PROTOCOL || effect === EFFECT_READ) return 'allow';

  const m = normalizeMode(mode);
  if (m === MODE_PLAN) return 'refuse';
  if (m !== MODE_MANUAL) return 'allow';

  // "Don't ask again for this tool" — a NAME-level grant, which is the coarse one
  // the user chose. Accepts a Set or an array so a caller holding either works.
  if (toolName && approvedTools) {
    const granted = approvedTools.has
      ? approvedTools.has(toolName)
      : Array.isArray(approvedTools) && approvedTools.includes(toolName);
    if (granted) return 'allow';
  }
  return 'ask';
}

/**
 * The same decision, given the whole tool call rather than a pre-read effect.
 *
 * This is what `chatRounds.js` uses, since it holds the SSE event. A thin wrapper
 * so the decision itself has ONE implementation and can be tested over effects
 * without constructing events.
 */
export function gateForCall(toolCall, { mode, approvedTools } = {}) {
  return decideToolGate({
    effect: effectOf(toolCall),
    mode,
    toolName: toolCall?.name,
    approvedTools,
  });
}

/**
 * The `tool_result` a plan-mode call gets. Mirrors `approval.py`'s wording.
 *
 * A REFUSE is information the model can act on, so it says what to do instead
 * rather than only what went wrong.
 */
export function planRefusalMessage(toolName) {
  return (
    `\`${toolName}\` is not available: this session is in PLAN mode, so nothing ` +
    `can be changed yet. Finish working out the approach and call \`set_plan\` ` +
    `with it. The user starts the work by approving that plan.`
  );
}

/**
 * The `tool_result` for a call the user turned down.
 *
 * Framed as a DECISION, not a failure, and it asks for a conversation rather than
 * a workaround: a model that reads "permission denied" as an obstacle looks for a
 * way around it, and the useful behaviour is to find out what the user wanted.
 */
export function rejectionMessage(toolName) {
  return (
    `The user declined this action, so \`${toolName}\` did not run. This is their ` +
    `decision, not an error — do not retry it and do not look for another way to ` +
    `achieve the same thing. Say briefly what you were going to do and why, then ` +
    `ask what they would prefer instead.`
  );
}
